import React from "react"
import { twMerge } from "tailwind-merge"

import { getElementTextColor } from "../utils/colorUtils"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

const GenericSection = ({ section, theme, highlightedField }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}
  const layout = settings.layout || "text-image"
  const fullWidth = settings.fullWidth || false
  const hasBackground = settings.hasBackground || false
  const showButton = settings.showButton || false

  // Get dynamic text colors based on background
  const sectionBackgroundColor = hasBackground
    ? theme?.colors?.bgLight || "#F5F5F5"
    : theme?.colors?.background || "#FFFFFF"
  const headingColor = getElementTextColor("heading", sectionBackgroundColor, theme)
  const bodyColor = getElementTextColor("body", sectionBackgroundColor, theme)

  const renderContent = () => {
    const contentElement = (
      <div>
        {section.body && (
          <>
            <style>{`
              .generic-section-content {
                text-align: initial !important;
              }
              .generic-section-content ul {
                list-style-type: disc !important;
                margin-left: 1.5rem !important;
                margin-bottom: 1rem !important;
              }
              .generic-section-content ol {
                list-style-type: decimal !important;
                margin-left: 1.5rem !important;
                margin-bottom: 1rem !important;
              }
              .generic-section-content li {
                margin-bottom: 0.5rem !important;
                display: list-item !important;
              }
              .generic-section-content .ql-align-right {
                text-align: right !important;
              }
              .generic-section-content .ql-align-center {
                text-align: center !important;
              }
              .generic-section-content .ql-align-justify {
                text-align: justify !important;
              }
              .generic-section-content .ql-align-left {
                text-align: left !important;
              }
              .generic-section-content a {
                color: ${theme?.colors?.primary || "#BD654F"} !important;
                text-decoration: underline !important;
              }
              .generic-section-content a:hover {
                color: ${theme?.colors?.primaryDark || "#A55A45"} !important;
                text-decoration: underline !important;
              }
              .generic-section-content p {
                margin-bottom: 1rem !important;
                margin-top: 0 !important;
              }
              .generic-section-content p:first-child {
                margin-top: 0 !important;
              }
              .generic-section-content p:last-child {
                margin-bottom: 0 !important;
              }
              .generic-section-content strong,
              .generic-section-content b {
                font-weight: ${theme?.fonts?.paragraphBoldWeight || "600"} !important;
              }
              .generic-section-content em,
              .generic-section-content i {
                font-style: italic !important;
              }
              .generic-section-content u {
                text-decoration: underline !important;
              }
              .generic-section-content h1 {
                font-family: ${theme?.fonts?.heading || "Playfair Display, serif"} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                font-size: 56px !important;
                line-height: 72px !important;
                margin-bottom: 1rem !important;
                margin-top: 1.5rem !important;
                color: ${bodyColor} !important;
              }
              .generic-section-content h2 {
                font-family: ${theme?.fonts?.heading || "Playfair Display, serif"} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                font-size: 40px !important;
                line-height: 48px !important;
                margin-bottom: 1rem !important;
                margin-top: 1.5rem !important;
                color: ${bodyColor} !important;
              }
              .generic-section-content h3 {
                font-family: ${theme?.fonts?.heading || "Playfair Display, serif"} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                font-size: 27px !important;
                line-height: 40px !important;
                margin-bottom: 1rem !important;
                margin-top: 1.5rem !important;
                color: ${bodyColor} !important;
              }
              .generic-section-content h4 {
                font-family: ${theme?.fonts?.heading || "Playfair Display, serif"} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                font-size: 20px !important;
                line-height: 32px !important;
                margin-bottom: 0.75rem !important;
                margin-top: 1.25rem !important;
                color: ${bodyColor} !important;
              }
              .generic-section-content h5 {
                font-family: ${theme?.fonts?.heading || "Playfair Display, serif"} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                font-size: 18px !important;
                line-height: 28px !important;
                margin-bottom: 0.5rem !important;
                margin-top: 1rem !important;
                color: ${bodyColor} !important;
              }
              .generic-section-content h6 {
                font-family: ${theme?.fonts?.heading || "Playfair Display, serif"} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                font-size: 16px !important;
                line-height: 24px !important;
                margin-bottom: 0.5rem !important;
                margin-top: 1rem !important;
                color: ${bodyColor} !important;
              }
              @media (max-width: 768px) {
                .generic-section-content h1 {
                  font-size: 32px !important;
                  line-height: 48px !important;
                }
                .generic-section-content h2 {
                  font-size: 27px !important;
                  line-height: 32px !important;
                }
                .generic-section-content h3 {
                  font-size: 22px !important;
                  line-height: 32px !important;
                }
                .generic-section-content h4 {
                  font-size: 18px !important;
                  line-height: 24px !important;
                }
                .generic-section-content h5 {
                  font-size: 16px !important;
                  line-height: 24px !important;
                }
                .generic-section-content h6 {
                  font-size: 14px !important;
                  line-height: 20px !important;
                }
              }
            `}</style>
            <div
              className="generic-section-content prose prose-lg max-w-none" // eslint-disable-line tailwindcss/no-custom-classname
              style={{
                fontFamily: theme?.fonts?.paragraph || "sans-serif",
                color: bodyColor
              }}
              dangerouslySetInnerHTML={{ __html: section.body }}
            />
          </>
        )}
        {showButton && settings.buttonText && (
          <div className="mt-6">
            <a
              href={settings.buttonLink || "#"}
              className={`inline-block rounded px-6 py-3 text-sm font-medium transition-colors ${
                settings.buttonStyle === "primary"
                  ? "bg-teal-600 text-white hover:bg-teal-700"
                  : settings.buttonStyle === "secondary"
                  ? "border border-teal-600 text-teal-600 hover:bg-teal-50"
                  : "text-teal-600 underline hover:text-teal-700"
              }`}>
              {settings.buttonText}
            </a>
          </div>
        )}
      </div>
    )

    const imageElement = section.imageUrl && (
      <div>
        <img
          src={section.imageUrl}
          alt={section.headline || section.layout}
          className="h-auto w-full rounded-lg shadow-lg"
        />
      </div>
    )

    switch (layout) {
      case "text-only":
        return contentElement
      case "image-text":
        return (
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
            {imageElement}
            {contentElement}
          </div>
        )
      case "centered":
        return (
          <div className="mx-auto max-w-3xl text-center">
            {contentElement}
            {imageElement}
          </div>
        )
      case "text-image":
      default:
        return (
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
            {contentElement}
            {imageElement}
          </div>
        )
    }
  }

  return (
    <section
      className={twMerge("px-4 py-16", fullWidth ? "px-0" : "", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
      style={{ backgroundColor: sectionBackgroundColor }}>
      <div className={twMerge("mx-auto", fullWidth ? "max-w-none px-4" : "max-w-4xl")}>
        {section.headline && (
          <h2
            className={twMerge("mb-6 text-3xl font-bold", layout === "centered" ? "text-center" : "text-left")}
            style={{
              fontFamily: theme?.fonts?.heading || "serif",
              fontWeight: theme?.fonts?.headingWeight || "700",
              color: headingColor
            }}>
            {section.headline}
          </h2>
        )}
        {section.subheadline && (
          <p
            className={twMerge("mb-8 text-lg", layout === "centered" ? "text-center" : "text-left")}
            style={{
              fontFamily: theme?.fonts?.paragraph || "sans-serif",
              color: bodyColor
            }}>
            {section.subheadline}
          </p>
        )}

        {renderContent()}
      </div>
    </section>
  )
}

export default GenericSection
